import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import apiService from '@/services/api';

const SERVICE_LABELS: Record<string, string> = {
  wdg_aluminium:            'Aluminium Windows & Doors',
  wdg_door_installation:    'Door Installation',
  wdg_double_triple_glazing:'Double/Triple Glazing',
  wdg_emergency_boarding:   'Emergency Boarding',
  wdg_glass_replacement:    'Glass Replacement',
  wdg_shopfront_glazing:    'Shopfront Glazing',
  wdg_timber_windows_doors: 'Timber Windows & Doors',
  wdg_upvc_installation:    'UPVC Installation',
  wdg_window_installation:  'Window Installation',
};

const AdminWindowsDoorsPricingRules = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allNodes, setAllNodes] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<string>('wdg_window_installation');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.request('/admin/pricing-extended/config/26', 'GET');
      if (res.success && Array.isArray(res.data)) {
        setAllNodes(res.data);
      } else {
        setError('Unexpected response format');
      }
    } catch {
      setError('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  const serviceIds = Object.keys(SERVICE_LABELS);
  const nodes = allNodes.filter(n => n.service_id === selectedService);
  const multiplierCols = nodes.length && nodes.find(n => n.options?.length)
    ? Object.keys(nodes.find(n => n.options?.length).options[0]).filter(k => k.endsWith('_multiplier'))
    : [];

  return (
    <div className="space-y-6 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Windows, Doors &amp; Glazing Pricing Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Sub-service selector */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Select Service:</p>
            <div className="flex flex-wrap gap-2">
              {serviceIds.map(sid => (
                <Button
                  key={sid}
                  variant={selectedService === sid ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setSelectedService(sid); setActiveTab('overview'); }}
                >
                  {SERVICE_LABELS[sid]}
                </Button>
              ))}
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="decision-tree">Decision Tree</TabsTrigger>
                <TabsTrigger value="multipliers">Multipliers</TabsTrigger>
                <TabsTrigger value="fixed-costs">Fixed Costs</TabsTrigger>
              </TabsList>

              {/* Overview */}
              <TabsContent value="overview">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{nodes.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Decision Nodes</p>
                      </div>
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{nodes.reduce((s, n) => s + (n.options?.length || 0), 0)}</p>
                        <p className="text-xs text-gray-500 mt-1">Total Options</p>
                      </div>
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{multiplierCols.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Multiplier Types</p>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Node Title</TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Options</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nodes.map(node => (
                          <TableRow key={node.id}>
                            <TableCell className="font-mono text-xs">{node.node_number}</TableCell>
                            <TableCell className="font-medium">{node.node_title}</TableCell>
                            <TableCell className="text-sm text-gray-600">{node.node_question}</TableCell>
                            <TableCell><Badge variant="secondary">{node.options?.length || 0}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Decision Tree */}
              <TabsContent value="decision-tree">
                <div className="space-y-4">
                  {nodes.map(node => (
                    <Card key={node.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">
                          Node {node.node_number}: {node.node_title}
                          {node.is_entry_node ? <Badge className="ml-2" variant="outline">Entry Node</Badge> : null}
                        </CardTitle>
                        <p className="text-xs text-gray-500 italic">{node.node_question}</p>
                      </CardHeader>
                      <CardContent>
                        {node.options?.length ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Key</TableHead>
                                <TableHead>Label</TableHead>
                                {multiplierCols.map(c => (
                                  <TableHead key={c} className="text-xs">{c.replace(/_multiplier$/, '').replace(/_/g, ' ')}</TableHead>
                                ))}
                                <TableHead>Add Cost</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {node.options.map((opt: any, oi: number) => (
                                <TableRow key={oi}>
                                  <TableCell className="font-mono text-xs">{opt.option_key}</TableCell>
                                  <TableCell>{opt.option_label}</TableCell>
                                  {multiplierCols.map(c => (
                                    <TableCell key={c} className={parseFloat(opt[c]) !== 1 ? 'font-semibold text-blue-700' : 'text-gray-400'}>
                                      {opt[c]}×
                                    </TableCell>
                                  ))}
                                  <TableCell className={opt.add_cost_gbp > 0 ? 'font-semibold text-green-700' : 'text-gray-400'}>
                                    {opt.add_cost_gbp > 0 ? `$${opt.add_cost_gbp}` : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-xs text-gray-400">No options configured</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Multipliers */}
              <TabsContent value="multipliers">
                <div className="space-y-4">
                  {multiplierCols.length === 0 ? (
                    <p className="text-sm text-gray-500">No multiplier columns found for this service.</p>
                  ) : nodes.filter(n => n.options?.length).map(node => (
                    <Card key={node.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Node {node.node_number}: {node.node_title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Option</TableHead>
                              {multiplierCols.map(c => (
                                <TableHead key={c} className="text-xs capitalize">{c.replace(/_multiplier$/, '').replace(/_/g, ' ')}</TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {node.options.map((opt: any, oi: number) => (
                              <TableRow key={oi}>
                                <TableCell className="font-medium">{opt.option_label}</TableCell>
                                {multiplierCols.map(c => (
                                  <TableCell key={c} className={parseFloat(opt[c]) !== 1 ? 'font-semibold text-blue-700' : 'text-gray-400'}>
                                    {opt[c]}×
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Fixed Costs */}
              <TabsContent value="fixed-costs">
                <div className="space-y-4">
                  {nodes.filter(n => n.options?.some((o: any) => parseFloat(o.add_cost_gbp) > 0)).length === 0 ? (
                    <p className="text-sm text-gray-500">No fixed cost additions configured for this service.</p>
                  ) : nodes.filter(n => n.options?.some((o: any) => parseFloat(o.add_cost_gbp) > 0)).map(node => (
                    <Card key={node.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Node {node.node_number}: {node.node_title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Option</TableHead>
                              <TableHead>Add Cost</TableHead>
                              <TableHead>Min</TableHead>
                              <TableHead>Max</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {node.options.filter((o: any) => parseFloat(o.add_cost_gbp) > 0).map((opt: any, oi: number) => (
                              <TableRow key={oi}>
                                <TableCell className="font-medium">{opt.option_label}</TableCell>
                                <TableCell className="font-semibold text-green-700">£{opt.add_cost_gbp}</TableCell>
                                <TableCell>{opt.add_cost_min_gbp ? `$${opt.add_cost_min_gbp}` : '—'}</TableCell>
                                <TableCell>{opt.add_cost_max_gbp ? `$${opt.add_cost_max_gbp}` : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWindowsDoorsPricingRules;
